import { useEffect, useRef } from 'react'
import { useOnboardingV2 } from '../OnboardingContext'

interface UseStepConfig {
  /** Drives the canContinue state in context. When false, Continue button is disabled. */
  isValid?: boolean

  /** Guard: if shouldSkip becomes true, auto-navigate to targetStep (fires once).
   *  Used by ScanMilestone for async-data-dependent skipping.
   *  Generating steps use goNext forward-skip in OnboardingContext instead. */
  guard?: {
    shouldSkip: boolean
    targetStep: number
  }

  /** Runs when user clicks Continue (before navigation). Return false to skip goNext. */
  beforeNext?: () => Promise<boolean | void>

  /** Override the shell nav Continue button text (null = use default). */
  continueLabel?: string

  /** Hide the shell nav Back button for this step. */
  hideBack?: boolean
}

export function useStep(config: UseStepConfig): void {
  const { setCanContinue, setBeforeNext, goToStep, setContinueLabel, setHideBackButton } = useOnboardingV2()

  // 1. Guard: skip when condition becomes true (re-evaluates as async data loads)
  //    Ref prevents double-fire in React StrictMode
  const guardFiredRef = useRef(false)
  useEffect(() => {
    if (config.guard?.shouldSkip && !guardFiredRef.current) {
      guardFiredRef.current = true
      goToStep(config.guard.targetStep)
    }
  }, [config.guard?.shouldSkip, config.guard?.targetStep, goToStep])

  // 2. isValid: sync to context's canContinue
  useEffect(() => {
    if (config.isValid !== undefined) {
      setCanContinue(config.isValid)
    }
    // Reset canContinue to true on unmount so next step starts enabled
    return () => {
      setCanContinue(true)
    }
  }, [config.isValid, setCanContinue])

  // 3. continueLabel: override shell nav button text
  useEffect(() => {
    setContinueLabel(config.continueLabel ?? null)
    return () => { setContinueLabel(null) }
  }, [config.continueLabel, setContinueLabel])

  // 4. hideBack: hide shell nav Back button
  useEffect(() => {
    setHideBackButton(config.hideBack ?? false)
    return () => { setHideBackButton(false) }
  }, [config.hideBack, setHideBackButton])

  // 5. beforeNext: register callback with context
  //    Ref ensures context always calls the latest closure
  const beforeNextRef = useRef(config.beforeNext)
  beforeNextRef.current = config.beforeNext

  useEffect(() => {
    if (config.beforeNext) {
      setBeforeNext(() => beforeNextRef.current!())
    }
    return () => {
      setBeforeNext(null)
    }
  }, [setBeforeNext])
}
